import { useState } from 'react';
import { today, getLocalTimeZone } from '@internationalized/date';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  DatePicker,
  DrawerFooter,
  SearchField,
  Stack,
  Row,
  Col,
  Button,
  Table,
  P,
  Badge,
  useToast,
} from '@workramp/ui';

import { useEnrollment } from '~/components/custom-hooks/useEnrollment';
import { useEventEnrollment } from '~/components/custom-hooks/useEventEnrollment';
import { useTeamLeadContactsQuery } from '~/components/custom-hooks/useTeamLeadContacts';
import { useDebouncedValue } from '~/hooks';
import { APP_CONSTANTS, I18n, getExternalUser } from '~/common/explicit-exports';

const TRANSLATED_TITLE = I18n.t('js.academy.team_enrollment.title');
const TRANSLATED_DUE_DATE_LABEL = I18n.t('js.academy.team_enrollment.due_date.label');
const TRANSLATED_DUE_DATE_PLACEHOLDER = I18n.t('js.academy.team_enrollment.due_date.placeholder');
const TRANSLATED_TEAM_MEMBERS_LABEL = I18n.t('js.academy.team_enrollment.team_members.label');
const TRANSLATED_CLEAR_BUTTON = I18n.t('js.academy.team_enrollment.team_members.clear_button');
const TRANSLATED_SEARCH_LABEL = I18n.t('js.academy.team_enrollment.search.label');
const TRANSLATED_SEARCH_PLACEHOLDER = I18n.t('js.academy.team_enrollment.search.placeholder');
const TRANSLATED_NO_CONTACTS_FOUND = I18n.t('js.academy.team_enrollment.table.empty_state');
const TRANSLATED_NAME_COLUMN = I18n.t('js.academy.team_enrollment.table.columns.name');
const TRANSLATED_EMAIL_COLUMN = I18n.t('js.academy.team_enrollment.table.columns.email');
const TRANSLATED_ENROLLED_BADGE = I18n.t('js.academy.team_enrollment.table.enrolled_badge');
const TRANSLATED_ATTENDING_BADGE = I18n.t('js.academy.team_enrollment.table.attending_badge');
const TRANSLATED_INVITED_BADGE = I18n.t('js.academy.team_enrollment.table.invited_badge');
const TRANSLATED_TOAST_SUCCESS = I18n.t('js.academy.team_enrollment.toast.success');
const TRANSLATED_TOAST_ERROR = I18n.t('js.academy.team_enrollment.toast.error');
const TRANSLATED_CANCEL_BUTTON = I18n.t('js.academy.team_enrollment.buttons.cancel');
const TRANSLATED_ENROLL_BUTTON = I18n.t('js.academy.team_enrollment.buttons.enroll');

const COLUMNS = [
  { key: 'name', name: TRANSLATED_NAME_COLUMN },
  { key: 'email', name: TRANSLATED_EMAIL_COLUMN },
];

export interface EnrollTeamDrawerProps {
  academyId: string;
  academyEventId?: string;
  trainingId: string;
  trainingType: string;
  isOpen: boolean;
  onClose?: () => void;
  showDueDate?: boolean;
}

export const EnrollTeamDrawer = ({
  academyId,
  academyEventId,
  trainingId,
  trainingType,
  isOpen,
  onClose,
  showDueDate,
}: EnrollTeamDrawerProps) => {
  const { open: openToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentDueDate, setCurrentDueDate] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const externalUser = getExternalUser();

  const { data: rows = [], isLoading: isLoadingContacts } = useTeamLeadContactsQuery(
    academyId,
    debouncedSearchTerm,
    trainingId,
    trainingType,
  );

  const enrolledUserIds = rows.filter((row) => row.status !== null).map((row) => row.id);

  const isEventTraining =
    trainingType === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_ON_DEMAND_CONTENT ||
    trainingType === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_SESSION;

  const { mutate: enrollUsers, isLoading: isEnrolling } = useEnrollment({
    onSuccess: () => {
      openToast({ children: TRANSLATED_TOAST_SUCCESS });
      onClose?.();
    },
    onError: () => {
      openToast({ children: TRANSLATED_TOAST_ERROR });
    },
  });

  const { mutate: enrollUsersToEvent, isLoading: isEnrollingEvent } = useEventEnrollment({
    onSuccess: () => {
      openToast({ children: TRANSLATED_TOAST_SUCCESS });
      onClose?.();
    },
    onError: () => {
      openToast({ children: TRANSLATED_TOAST_ERROR });
    },
  });

  const handleSubmit = () => {
    const selectedRows = rows.filter((row) => selectedUsers.has(row.id));

    if (isEventTraining) {
      enrollUsersToEvent({
        external_user_ids: selectedRows.map((user) => user.id),
        academy_event_id: academyEventId,
        ...(trainingType === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_ON_DEMAND_CONTENT && {
          academy_event_on_demand_content_id: trainingId,
        }),
        ...(trainingType === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_SESSION && {
          academy_event_session_id: trainingId,
        }),
      });
    } else {
      enrollUsers({
        training_id: trainingId,
        training_type: trainingType,
        external_user_ids: selectedRows.map((user) => user.id),
        emails: selectedRows.map((user) => user.email),
        due_at: currentDueDate
          ? new Date(currentDueDate.year, currentDueDate.month - 1, currentDueDate.day).toISOString()
          : null,
      });
    }
  };

  const onSelectionChange = (value) => {
    setSelectedUsers(value);
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailRegex.test(email);
  };

  const getStatusBadgeText = (status: string | null) => {
    switch (status) {
      case 'attending':
        return TRANSLATED_ATTENDING_BADGE;
      case 'invited':
        return TRANSLATED_INVITED_BADGE;
      case 'enrolled':
        return TRANSLATED_ENROLLED_BADGE;
      default:
        return null;
    }
  };

  const handleInviteEmail = () => {
    if (isEventTraining) {
      enrollUsersToEvent({
        emails: [searchTerm],
        academy_event_id: academyEventId,
        ...(trainingType === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_ON_DEMAND_CONTENT && {
          academy_event_on_demand_content_id: trainingId,
        }),
        ...(trainingType === APP_CONSTANTS.NON_ASSET_PICKER_TYPES.ACADEMY_EVENT_SESSION && {
          academy_event_session_id: trainingId,
        }),
      });
    } else {
      enrollUsers({
        training_id: trainingId,
        training_type: trainingType,
        emails: [searchTerm],
        due_at: currentDueDate
          ? new Date(currentDueDate.year, currentDueDate.month - 1, currentDueDate.day).toISOString()
          : null,
      });
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose}>
      <DrawerHeader title={TRANSLATED_TITLE} />
      <DrawerBody>
        <Stack size={8}>
          {showDueDate && (
            <Row>
              <Stack size={2}>
                <P>
                  <b>{TRANSLATED_DUE_DATE_LABEL}</b>
                </P>
                <DatePicker
                  hideLabel
                  allowClear
                  forceShowYear
                  label={TRANSLATED_DUE_DATE_LABEL}
                  placeholder={TRANSLATED_DUE_DATE_PLACEHOLDER}
                  value={currentDueDate}
                  onChange={setCurrentDueDate}
                  minValue={today(getLocalTimeZone())}
                />
              </Stack>
            </Row>
          )}
          <Stack size={2}>
            <Row justify="space-between" align="center">
              <Col xs="auto">
                <b>{TRANSLATED_TEAM_MEMBERS_LABEL}</b>
              </Col>
              <Col xs="auto">
                <Row gapX={2} align="center">
                  <Col xs="auto">
                    <P>
                      <b>
                        {I18n.t('js.academy.team_enrollment.team_members.selected_count', {
                          count: selectedUsers.size,
                        })}
                      </b>
                    </P>
                  </Col>
                  <Col xs="auto">
                    <Button variant="outline" color="secondary" onPress={clearSelection}>
                      {TRANSLATED_CLEAR_BUTTON}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row>
              <Col xs="fill">
                <SearchField
                  label={TRANSLATED_SEARCH_LABEL}
                  placeholder={TRANSLATED_SEARCH_PLACEHOLDER}
                  hideLabel
                  value={searchTerm}
                  onChange={setSearchTerm}
                />
              </Col>
            </Row>
            <Row>
              <Col xs="fill">
                <Table
                  emptyState={
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      {isValidEmail(searchTerm) && rows.length === 0 && externalUser.teamLeadCanInviteNewContacts ? (
                        <Stack size={3}>
                          <P>{TRANSLATED_NO_CONTACTS_FOUND}</P>
                          <Button color="primary" onPress={handleInviteEmail}>
                            Invite &apos;{searchTerm}&apos;
                          </Button>
                        </Stack>
                      ) : (
                        TRANSLATED_NO_CONTACTS_FOUND
                      )}
                    </div>
                  }
                  rowPadding={32}
                  isLoading={isLoadingContacts}
                  selectionMode="multiple"
                  bulkSelectionMode="page"
                  allowsItemsPerPage
                  itemsPerPageOptions={[25, 50, 100]}
                  selectedKeys={selectedUsers}
                  onSelectionChange={onSelectionChange}
                  disabledKeys={enrolledUserIds}
                >
                  <Table.Header columns={COLUMNS}>
                    {(column) => (
                      <Table.Column key={column.key} {...column.props}>
                        {column.name}
                      </Table.Column>
                    )}
                  </Table.Header>
                  <Table.Body items={rows}>
                    {(item) => (
                      <Table.Row key={item.id}>
                        {(columnKey) => (
                          <Table.Cell key={columnKey}>
                            {columnKey === 'name' ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px' }}>{item[columnKey]}</span>
                                {getStatusBadgeText(item.status) && (
                                  <Badge variant="secondary" size="sm">
                                    {getStatusBadgeText(item.status)}
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              item[columnKey]
                            )}
                          </Table.Cell>
                        )}
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </Col>
            </Row>
          </Stack>
        </Stack>
      </DrawerBody>
      <DrawerFooter>
        <Row gapX={2} justify="flex-end">
          <Col xs="auto">
            <Row gapX={2}>
              <Col xs="auto">
                <Button data-testid="cancel-button" color="secondary" variant="text" onPress={onClose}>
                  {TRANSLATED_CANCEL_BUTTON}
                </Button>
              </Col>
              <Col xs="auto">
                <Button
                  data-testid="save-button"
                  type="submit"
                  onPress={handleSubmit}
                  isDisabled={
                    selectedUsers.size === 0 || isEnrolling || isEnrollingEvent || !trainingId || !trainingType
                  }
                >
                  {TRANSLATED_ENROLL_BUTTON}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </DrawerFooter>
    </Drawer>
  );
};
